import { useEffect } from 'react';

// Hook for handling page reload and minimize
export const usePageStateHandler = () => {
  useEffect(() => {
    let isRefreshing = false;

    const handleBeforeUnload = (e) => {
      // Set a flag in sessionStorage to indicate refresh
      sessionStorage.setItem('isRefreshing', 'true');
      isRefreshing = true;
    };

    const handlePageHide = (e) => {
      // Clear the flag if it's not a refresh
      if (!e.persisted) {
        sessionStorage.removeItem('isRefreshing');
        isRefreshing = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Check if this is a minimize
        const isRefreshing = sessionStorage.getItem('isRefreshing');
        if (!isRefreshing) {
          // This is a minimize, preserve the token
          sessionStorage.setItem('isMinimized', 'true');
        }
      } else {
        // Tab is visible again
        sessionStorage.removeItem('isMinimized');
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};

// Hook for handling tab and browser close
export const useTabCloseHandler = () => {
  useEffect(() => {
    // Track if this is a page refresh
    let isRefreshing = false;

    // Handle page refresh
    const handleBeforeUnload = (e) => {
      // Set refresh flag
      isRefreshing = true;
      sessionStorage.setItem('isRefreshing', 'true');
    };

    // Handle visibility changes (minimize vs close)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Page is being hidden
        const refreshFlag = sessionStorage.getItem('isRefreshing');
        
        if (!refreshFlag) {
          // Not a refresh, store the time
          sessionStorage.setItem('hiddenTime', Date.now().toString());
          // Set minimize flag
          sessionStorage.setItem('isMinimized', 'true');
        }
      } else {
        // Page is visible again
        const hiddenTime = sessionStorage.getItem('hiddenTime');
        const refreshFlag = sessionStorage.getItem('isRefreshing');
        const isMinimized = sessionStorage.getItem('isMinimized');
        
        if (hiddenTime && !refreshFlag) {
          const timeHidden = Date.now() - parseInt(hiddenTime);
          
          // Only clear session if it was not a minimize action and hidden for more than 1 second
          if (!isMinimized && timeHidden > 1000) {
            // Clear session data
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('tokenCreationTime');
          }
          
          // Clean up
          sessionStorage.removeItem('hiddenTime');
          sessionStorage.removeItem('isMinimized');
          sessionStorage.removeItem('isRefreshing');
        }
      }
    };

    // Handle browser close or navigation away
    const handlePageHide = (e) => {
      const refreshFlag = sessionStorage.getItem('isRefreshing');
      
      // Only clear session if it's not a refresh and not cached
      if (!e.persisted && !refreshFlag) {
        // Clear session data
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('tokenCreationTime');
      }
      
      // Clear refresh flag if it's not a refresh
      if (!e.persisted) {
        sessionStorage.removeItem('isRefreshing');
        isRefreshing = false;
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);
}; 